import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Apiservice } from '../../../services/ApiService';
import { VenueService } from '../../../services/VenueService';
import { StorageService } from '../../../services/StorageService';
import { ExcelService } from '../../../services/ExcelService';
import { PermissionsService } from '../../../services/Permissions';
import { Performance } from '../../../models/performance.model';

interface VenueOption {
  key: string;
  value: { name: string; timezone?: string };
}

type StageCode = 'EL' | 'SR' | 'TC' | 'TM1' | 'TST';
type StageState = 'done' | 'progress' | 'none';

const STAGE_CODES: StageCode[] = ['EL', 'SR', 'TC', 'TM1', 'TST'];

const STAGE_STATES: Record<string, StageState[]> = {
  event_listing_loaded: ['progress', 'none', 'none', 'none', 'none'],
  rules_set:            ['done', 'progress', 'none', 'none', 'none'],
  test_config:          ['done', 'done', 'progress', 'none', 'none'],
  client_confirmed:     ['done', 'done', 'done', 'progress', 'none'],
  tm1_entry_configured: ['done', 'done', 'done', 'done', 'progress'],
  testing:              ['done', 'done', 'done', 'done', 'progress'],
  event_day_ready:      ['done', 'done', 'done', 'done', 'done'],
  ready:                ['done', 'done', 'done', 'done', 'done']
};

interface StatusPill {
  label: string;
  variant: 'ready' | 'testing' | 'warn';
}

const STATUS_PILL: Record<string, StatusPill> = {
  event_listing_loaded: { label: 'Event listing', variant: 'testing' },
  rules_set:            { label: 'Rules set',     variant: 'testing' },
  test_config:          { label: 'Test config ⚠', variant: 'warn' },
  client_confirmed:     { label: 'Client confirmed', variant: 'ready' },
  tm1_entry_configured: { label: 'TM1 configured',   variant: 'ready' },
  testing:              { label: 'Testing',          variant: 'testing' },
  event_day_ready:      { label: 'Event day ready',  variant: 'ready' },
  ready:                { label: 'Ready',            variant: 'ready' }
};

const DAY_MS = 24 * 60 * 60 * 1000;

@Component({
  selector: 'app-performance',
  templateUrl: './performance.component.html',
  standalone: false,
  styleUrls: ['./performance.component.scss', '../styles/shared.scss']
})
export class PerformanceComponent implements OnInit, OnDestroy {
  loading = false;
  loadError = '';
  venuelist: VenueOption[] = [];
  selectedVenue = '';
  venueTimezone = '';
  performances: Performance[] = [];
  isAdding = false;

  isGlobalAdmin = false;
  isTicketmaster = false;
  alertsTarget: Performance | null = null;
  editTarget: Performance | null = null;
  deleteTarget: Performance | null = null;
  deleteSaving = false;
  deleteError = '';
  private permissionSubs: { unsubscribe(): void }[] = [];

  readonly stageCodes = STAGE_CODES;

  weekStart: Date = this.startOfWeek(new Date());

  constructor(
    private apiservice: Apiservice,
    private venueService: VenueService,
    private storage: StorageService,
    private router: Router,
    private excelService: ExcelService,
    private permissions: PermissionsService
  ) {}

  ngOnDestroy(): void {
    this.permissionSubs.forEach((s) => s.unsubscribe());
    this.permissionSubs = [];
  }

  openAlerts(performance: Performance, event: MouseEvent) {
    event.stopPropagation();
    if (!this.isGlobalAdmin) { return; }
    this.alertsTarget = performance;
  }

  closeAlerts() {
    this.alertsTarget = null;
  }

  openEdit(performance: Performance, event: MouseEvent) {
    event.stopPropagation();
    if (!this.isTicketmaster) { return; }
    this.editTarget = performance;
  }

  closeEdit() {
    this.editTarget = null;
  }

  onEditSaved() {
    this.editTarget = null;
    this.loadPerformances();
  }

  openDelete(performance: Performance, event: MouseEvent) {
    event.stopPropagation();
    if (!this.isTicketmaster) { return; }
    this.deleteTarget = performance;
    this.deleteError = '';
  }

  cancelDelete() {
    if (this.deleteSaving) { return; }
    this.deleteTarget = null;
    this.deleteError = '';
  }

  confirmDelete() {
    if (!this.deleteTarget || !this.isTicketmaster) { return; }
    const target = this.deleteTarget;
    this.deleteSaving = true;
    this.deleteError = '';
    this.apiservice.deletePerformance(this.selectedVenue, Number(target.item)).subscribe({
      next: () => {
        this.deleteSaving = false;
        this.deleteTarget = null;
        this.loadPerformances();
      },
      error: (err) => {
        this.deleteSaving = false;
        this.deleteError = typeof err?.error === 'string'
          ? err.error
          : (err?.error?.message || err?.message || 'Failed to delete performance.');
      }
    });
  }

  get hasActions(): boolean {
    return this.isGlobalAdmin || this.isTicketmaster;
  }

  get otherPerformances(): Performance[] {
    if (!this.alertsTarget) { return []; }
    const targetItem = Number(this.alertsTarget.item);
    return this.performances.filter((p) => Number(p.item) !== targetItem);
  }

  downloadPerformances() {
    const items = this.filteredPerformances;
    if (!items.length) { return; }
    const venueName = this.venuelist.find((v) => v.key === this.selectedVenue)?.value?.name ?? this.selectedVenue;

    const rows = items.map((p) => {
      const stages = STAGE_CODES.map((_, i) => this.stageState(p, i));
      return {
        'Date': this.formatDate(p.item),
        'Time': this.formatTime(p.item),
        'Performance': p.description || '',
        'Status': this.statusPill(p).label,
        'Status Code': p.status || '',
        'EL': stages[0],
        'TC': stages[1],
        'TM1': stages[2],
        'TST': stages[3],
        'Last Updated': this.formatLastUpdated(p),
        'Hidden Events': (p.hiddenEvents ?? []).join('; ')
      };
    });

    const safeVenue = venueName.replace(/[^a-z0-9]+/gi, '_');
    const safeWeek = this.weekLabel.replace(/[^a-z0-9]+/gi, '_').replace(/^_|_$/g, '');
    this.excelService.exportAsExcelFile(rows, `performances_${safeVenue}_${safeWeek}`);
  }

  openDetail(performance: Performance) {
    if (!this.selectedVenue) { return; }
    this.router.navigate(['/performance', this.selectedVenue, performance.item]);
  }

  ngOnInit(): void {
    this.permissionSubs.push(
      this.permissions.globalAdmin.subscribe((val) => { this.isGlobalAdmin = !!val; })
    );
    this.permissionSubs.push(
      this.permissions.ticketmaster.subscribe((val) => { this.isTicketmaster = !!val; })
    );

    this.loading = true;
    this.venueService.getVenueList().subscribe({
      next: (data) => {
        this.venuelist = data as VenueOption[];
        const saved = this.storage.getVenue();
        if (saved && this.venuelist.some((v) => v.key === saved)) {
          this.selectedVenue = saved;
          this.onVenueChange();
        } else {
          this.loading = false;
        }
      },
      error: () => {
        this.loadError = 'Failed to load venue list.';
        this.loading = false;
      }
    });
  }

  onVenueChange() {
    if (!this.selectedVenue) {
      this.performances = [];
      return;
    }
    this.storage.saveVenue(this.selectedVenue);
    const selected = this.venuelist.find((v) => v.key === this.selectedVenue);
    this.venueTimezone = selected?.value?.timezone ?? '';
    this.loadPerformances();
  }

  loadPerformances() {
    this.loading = true;
    this.loadError = '';
    this.apiservice.getPerformances(this.selectedVenue).subscribe({
      next: (rows) => {
        this.performances = rows ?? [];
        this.backfillMissingStatuses();
        this.loading = false;
      },
      error: (err) => {
        this.loadError = err?.status === 401
          ? 'You do not have permission to view performances for this venue.'
          : 'Failed to load performances. Please try again.';
        this.loading = false;
      }
    });
  }

  private backfillMissingStatuses() {
    const venue = this.selectedVenue;
    const targets = this.performances.filter((p) => !p.status);
    if (!targets.length) { return; }

    for (const performance of targets) {
      performance.status = 'event_listing_loaded';
      this.apiservice.postPerformance({
        venueID: venue,
        timestamp: Number(performance.item),
        description: performance.description ?? '',
        status: 'event_listing_loaded',
        hiddenEvents: performance.hiddenEvents ?? []
      }).subscribe({ error: () => {} });
    }
  }

  openAdd() {
    if (!this.selectedVenue) { return; }
    this.isAdding = true;
  }

  onAddCancelled() {
    this.isAdding = false;
  }

  onAddSaved() {
    this.isAdding = false;
    this.loadPerformances();
  }

  prevWeek() {
    this.weekStart = new Date(this.weekStart.getTime() - 7 * DAY_MS);
  }

  nextWeek() {
    this.weekStart = new Date(this.weekStart.getTime() + 7 * DAY_MS);
  }

  goToday() {
    this.weekStart = this.startOfWeek(new Date());
  }

  get weekEnd(): Date {
    return new Date(this.weekStart.getTime() + 6 * DAY_MS);
  }

  get weekLabel(): string {
    const start = this.weekStart;
    const end = this.weekEnd;
    const sameMonth = start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear();
    const startFmt = new Intl.DateTimeFormat('en-AU', {
      day: 'numeric',
      month: sameMonth ? undefined : 'short'
    }).format(start);
    const endFmt = new Intl.DateTimeFormat('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    }).format(end);
    return `${startFmt} – ${endFmt}`;
  }

  get filteredPerformances(): Performance[] {
    const startEpoch = Math.floor(this.weekStart.getTime() / 1000);
    const endEpoch = Math.floor((this.weekEnd.getTime() + DAY_MS) / 1000);
    return this.performances.filter((p) => p.item >= startEpoch && p.item < endEpoch);
  }

  stageState(performance: Performance, index: number): StageState {
    const map = STAGE_STATES[(performance.status || '').toLowerCase()];
    if (!map) { return 'none'; }
    return map[index];
  }

  statusPill(performance: Performance): StatusPill {
    const key = (performance.status || '').toLowerCase();
    return STATUS_PILL[key] ?? { label: 'Scheduled', variant: 'testing' };
  }

  formatDate(epochSeconds: number): string {
    const tz = this.venueTimezone || 'UTC';
    return new Intl.DateTimeFormat('en-AU', {
      timeZone: tz,
      weekday: 'short',
      day: '2-digit',
      month: 'short'
    }).format(new Date(epochSeconds * 1000));
  }

  formatTime(epochSeconds: number): string {
    const tz = this.venueTimezone || 'UTC';
    return new Intl.DateTimeFormat('en-AU', {
      timeZone: tz,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }).format(new Date(epochSeconds * 1000));
  }

  relativeWhen(performance: Performance): { label: string; variant: 'today' | 'soon' | 'past' | '' } {
    const now = Date.now() / 1000;
    const ts = Number(performance.item);
    const diffSec = ts - now;
    const dayMs = 24 * 60 * 60;
    if (Math.abs(diffSec) < dayMs && this.isSameDayInVenue(ts, now)) {
      return { label: 'Today', variant: 'today' };
    }
    if (diffSec < 0) {
      return { label: 'Past', variant: 'past' };
    }
    if (diffSec < dayMs) {
      return { label: 'Tomorrow', variant: 'soon' };
    }
    if (diffSec < 7 * dayMs) {
      const days = Math.ceil(diffSec / dayMs);
      return { label: `In ${days} days`, variant: 'soon' };
    }
    return { label: '', variant: '' };
  }

  private isSameDayInVenue(ts1: number, ts2: number): boolean {
    const tz = this.venueTimezone || 'UTC';
    const fmt = new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' });
    return fmt.format(new Date(ts1 * 1000)) === fmt.format(new Date(ts2 * 1000));
  }

  customCount(performance: Performance): number {
    const a = Array.isArray(performance.customArchticsEvents) ? performance.customArchticsEvents.length : 0;
    const h = Array.isArray(performance.customHostEvents) ? performance.customHostEvents.length : 0;
    return a + h;
  }

  formatLastUpdated(performance: Performance): string {
    const history = performance.status_history;
    if (!history || !history.length) { return '—'; }
    const last = history[history.length - 1];
    const tz = this.venueTimezone || 'UTC';
    return new Intl.DateTimeFormat('en-AU', {
      timeZone: tz,
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }).format(new Date(last.time * 1000));
  }

  private startOfWeek(d: Date): Date {
    const copy = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const day = copy.getDay();
    const offset = (day + 6) % 7;
    copy.setDate(copy.getDate() - offset);
    return copy;
  }

  trackVenue(_idx: number, venue: VenueOption) { return venue.key; }
  trackPerformance(_idx: number, perf: Performance) { return perf.item; }
  trackStageCode(_idx: number, code: string) { return code; }
}
